<template>
  <div class="tc-app-header z-40 w-full h-16 bg-primary">
    <header
      class="z-40 mx-auto w-full max-w-screen-xl h-16 text-left sm:text-center"
    >
      <div
        class="tc-app-header__bar sm:flex sm:items-center sm:justify-between sm:h-16 bg-primary"
      >
        <div class="flex justify-between items-center px-6 py-4">
          <div v-if="!childOrganisations">
            <router-link
              v-if="isAuthorised"
              class="flex items-center text-white sm:justify-between"
              :to="{ name: 'client-assessments-list' }"
            >
              <component :is="siteLogo" />
            </router-link>
            <a
              v-else
              class="flex items-center text-white sm:justify-between"
              href="/"
            >
              <component :is="siteLogo" />
            </a>
          </div>
          <div
            v-if="childOrganisations && childOrganisation"
            class="my-1"
          >
            <Dropdown class="hidden sm:block">
              <template
                #trigger="{ hasChildOrganisationsFocus, isChildOrganisationsOpen }"
              >
                <span
                  class="block flex overflow-hidden justify-center items-center px-2 h-10 tracking-wide rounded border-2 text-primary"
                  :class="[
                    hasChildOrganisationsFocus || isChildOrganisationsOpen
                      ? 'border-white xl:border-secondary'
                      : 'border-gray-600 xl:border-gray-300',
                  ]"
                >
                  <span>{{ childOrganisation.name }}</span>
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="ml-2 w-6 h-6 chevron-down"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </span>
              </template>
              <template #dropdown-left>
                <ul
                  class="py-6 mt-6 w-64 text-left bg-white shadow-2xl xl:border"
                >
                  <li
                    v-for="child in childOrganisations"
                    :key="child.id"
                  >
                    <button
                      class="inline-block px-6 py-2 text-gray-600"
                      @click="changeChildOrganisation(child)"
                    >
                      {{ child.name }}
                    </button>
                  </li>
                </ul>
              </template>
            </Dropdown>
          </div>

          <div class="flex sm:hidden">
            <button
              type="button"
              class="px-2 text-black text-gray-500 duration-150 hover:text-primary focus:outline"
              :aria-label="isOpen ? 'Close navigation' : 'Open navigation'"
              :aria-expanded="String(isOpen)"
              aria-controls="main-navigation"
              @click="toggle"
            >
              <svg
                class="w-8 h-8 fill-current"
                viewBox="0 0 24 24"
              >
                <path
                  v-if="isOpen"
                  class="duration-200"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"
                />
                <path
                  v-if="!isOpen"
                  fill-rule="evenodd"
                  d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                />
              </svg>
            </button>
          </div>
        </div>

        <nav
          id="main-navigation"
          class="px-2 border-b duration-200 sm:flex sm:items-center sm:px-4 xl:justify-between border-secondary sm:px-0 sm:border-0"
          :class="{ hidden: !isOpen, block: isOpen }"
          @keydown.esc="isOpen = false"
        >
          <ul
            v-if="!isAuthorised"
            class="px-6 -mx-4 sm:flex sm:items-center sm:px-0 text-primary"
          >
            <li class="p-4 sm:py-0 lg:px-6">
              <a
                href="/product/"
                class="inline-block duration-150 underline-from-center"
              >
                Product
              </a>
            </li>
            <li
              v-if="usesAptitudePage"
              class="p-4 sm:py-0 lg:px-6"
            >
              <a
                href="/tests/"
                class="inline-block duration-150 underline-from-center"
              >
                Tests
              </a>
            </li>
            <li
              v-if="usesAssessments"
              class="p-4 sm:py-0 lg:px-6"
            >
              <a
                href="/our-assessments/"
                class="inline-block duration-150 underline-from-center"
              >
                Assessments
              </a>
            </li>
            <li
              v-if="usesPlansMenuItem"
              class="p-4 sm:py-0 lg:px-6"
            >
              <a
                href="/plans/"
                class="inline-block duration-150 underline-from-center"
              >
                Plans
              </a>
            </li>
            <li
              v-if="usesScience"
              class="p-4 sm:py-0 lg:px-6"
            >
              <a
                href="/science/"
                class="inline-block duration-150 underline-from-center"
              >
                Science
              </a>
            </li>
            <li class="p-4 sm:py-0 lg:px-6">
              <a
                href="/clients/"
                class="inline-block duration-150 underline-from-center"
              >
                Case Studies
              </a>
            </li>
            <li class="p-4 sm:py-0 lg:px-6">
              <a
                href="/magazine/"
                class="inline-block duration-150 underline-from-center"
              >
                Blog
              </a>
            </li>
            <li class="p-4 sm:py-0 lg:px-6">
              <router-link
                :to="{ name: 'sign-in' }"
                class="inline-block duration-150 underline-from-center"
              >
                Sign In
              </router-link>
            </li>
          </ul>

          <ul
            v-if="isAuthorised"
            class="px-6 -mx-4 sm:flex sm:items-center sm:px-0 text-primary signed-out:hidden"
          >
            <HeaderLink
              v-if="specialFeatures.includes('talentSource')"
              :to="{ name: 'source' }"
            >
              Source
            </HeaderLink>
            <HeaderLink
              v-if="!usesJobsLabels"
              :to="{ name: 'client-assessments-list' }"
            >
              Assessments
            </HeaderLink>
            <HeaderLink
              v-else-if="usesJobsLabels"
              :to="{ name: 'client-assessments-list' }"
            >
              Jobs
            </HeaderLink>
            <HeaderLink
              v-if="usesCandidatesList"
              :to="{ name: 'candidates' }"
            >
              Candidates
            </HeaderLink>
            <HeaderLink :to="{ name: 'results' }">
              Results
            </HeaderLink>
            <HeaderLink :to="{ name: 'client-exams-list' }">
              Tests
            </HeaderLink>
            <li
              v-if="
                hasOrganisationLoaded &&
                  haveSubscriptionsLoaded &&
                  !hasActiveSubscription &&
                  !unlimitedResults &&
                  isPAYG === false &&
                  !isPerCandidate
              "
              class="p-2 pb-4 w-32 sm:pb-2 lg:px-6 sm:w-auto"
            >
              <router-link
                :to="billingSettingsRoute"
                class="flex items-center px-4 h-10 font-bold tracking-wide uppercase rounded border-2 duration-150 text-secondary border-secondary hover:text-secondary-400 hover:border-secondary-400"
              >
                Upgrade
              </router-link>
            </li>
            <li
              v-if="satellite === 'testcandidates' && isPerCandidate && candidateBilling && !candidateBilling.blocked"
              class="p-2 pb-4 sm:w-32 sm:pb-2 lg:px-6 sm:w-auto"
              data-test="add-card-link"
              :class="{'tc-header-upgrade': satellite === 'testcandidates'}"
            >
              <router-link
                :to="billingSettingsRoute"
                class="flex items-center px-4 space-x-2 h-10 rounded border-2 duration-150 text-secondary border-secondary hover:text-secondary-400 hover:border-secondary-400"
              >
                <span class="font-bold tracking-wide uppercase">{{ candidateBilling && candidateBilling.pending_results && candidateBilling.pending_results.candidates ? `Unlock ${candidateBilling.pending_results.candidates} results` : 'Upgrade' }}</span>
              </router-link>
            </li>
            <li
              v-else-if="isPerCandidate && candidateBilling && candidateBilling.blocked"
              class="p-2 pb-4 sm:w-32 sm:pb-2 lg:px-6 sm:w-auto"
              data-test="update-card-link"
            >
              <router-link
                :to="billingSettingsRoute"
                class="flex items-center px-4 space-x-2 h-10 rounded border-2 duration-150 text-red-700 border-red-700 hover:text-red-500 hover:border-red-500"
              >
                <span class="font-bold tracking-wide uppercase">Update card</span>
              </router-link>
            </li>
            <li
              v-else-if="!isPerCandidate && !hasPaygUnlockedExams && !paygSubscription && !unlimitedResults && isPAYG"
              class="p-2 pb-4 sm:w-32 sm:pb-2 lg:px-6 sm:w-auto"
            >
              <router-link
                :to="billingSettingsRoute"
                class="flex items-center px-4 space-x-2 h-10 rounded border-2 duration-150 text-secondary border-secondary hover:text-secondary-400 hover:border-secondary-400"
              >
                <span
                  v-if="trialText"
                  class="text-sm leading-none"
                >
                  {{ trialText }}
                </span>
                <span class="font-bold tracking-wide uppercase">Upgrade</span>
              </router-link>
            </li>
            <li
              class="p-2 border-t border-gray-800 lg:px-4 sm:hidden signed-out:hidden"
            >
              <ul v-if="satellite === 'testcandidates'">
                <li class="py-3">
                  {{ name }}
                </li>
                <li
                  v-for="item in accountSections"
                  :key="item.route"
                  class="py-3"
                >
                  <router-link :to="{ name: item.route }">
                    {{ item.label }}
                  </router-link>
                </li>
                <li class="py-3">
                  <router-link :to="{ name: 'help-support' }">
                    Help &amp; support
                  </router-link>
                </li>
                <li class="py-3 border-t">
                  <router-link :to="{ name: 'sign-out' }">
                    Sign out
                  </router-link>
                </li>
              </ul>
              <ul v-else>
                <li class="py-4">
                  <div class="flex items-center sm:hidden">
                    <span class="text-xl font-semibold sm:hidden">{{
                      name
                    }}</span>
                  </div>
                </li>
                <li class="py-4">
                  <router-link
                    v-if="isPAYG"
                    :to="{ name: 'results' }"
                    class="inline-block duration-150 underline-from-center"
                  >
                    Results
                  </router-link>
                </li>
                <li class="py-4">
                  <router-link
                    :to="billingSettingsRoute"
                    class="inline-block duration-150 underline-from-center"
                  >
                    Plan
                  </router-link>
                </li>
                <li class="py-4">
                  <router-link
                    :to="{ name: 'settings-profile' }"
                    class="inline-block duration-150 underline-from-center"
                  >
                    Settings
                  </router-link>
                </li>
                <li class="py-4">
                  <a
                    href="https://support.testcandidates.com/"
                    target="_blank"
                    class="inline-block duration-150 underline-from-center"
                  >
                    Support
                  </a>
                </li>
                <li
                  v-if="usesFaqForSupport"
                  class="py-4"
                >
                  <!--
                    Was hardcoded to support.picked.ai, which is dead — its DNS points at a
                    deleted Netlify app and it fails with a TLS warning. This branch only
                    renders when the 'faq-support' feature is on, and that is currently
                    enabled for no satellite, so nobody saw it. Fixed anyway so it cannot
                    surface broken if the feature is ever turned on.
                  -->
                  <a
                    :href="supportUrl"
                    target="_blank"
                    class="inline-block duration-150 underline-from-center"
                  >
                    Support
                  </a>
                </li>
                <li class="py-4">
                  <router-link
                    :to="{ name: 'sign-out' }"
                    class="inline-block duration-150 underline-from-center"
                  >
                    Sign Out
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>

          <ul
            v-if="isAuthorised"
            class="hidden sm:flex sm:items-center text-primary signed-out:hidden"
          >
            <li class="p-2 sm:px-4 sm:ml-2">
              <Dropdown class="hidden sm:block">
                <template #trigger="{ hasFocus, isOpen }">
                  <span
                    class="block flex overflow-hidden justify-center items-center w-10 h-10 tracking-wide rounded border-2"
                    :class="[
                      hasFocus || isOpen
                        ? 'border-white xl:border-secondary'
                        : 'border-gray-600 xl:border-gray-300',
                    ]"
                  >
                    <span>{{ nameToInitials(name) }}</span>
                  </span>
                </template>
                <template #dropdown>
                  <ul
                    v-if="satellite === 'testcandidates'"
                    class="tc-account-dropdown py-4 mt-4 w-64 text-left bg-white shadow-xl border rounded-lg"
                  >
                    <li class="px-6 pb-4 border-b">
                      <strong class="block">{{ name }}</strong><span
                        class="tc-account-email text-sm text-gray-600"
                        :title="email"
                      >{{ email }}</span>
                    </li>
                    <li
                      v-for="item in accountSections"
                      :key="item.route"
                    >
                      <router-link
                        :to="{ name: item.route }"
                        class="block px-6 py-2"
                      >
                        {{ item.label }}
                      </router-link>
                    </li>
                    <li class="mt-2 pt-2 border-t">
                      <router-link
                        :to="{ name: 'help-support' }"
                        class="block px-6 py-2"
                      >
                        Help &amp; support
                      </router-link>
                    </li>
                    <li class="mt-2 pt-2 border-t">
                      <router-link
                        :to="{ name: 'sign-out' }"
                        class="block px-6 py-2"
                      >
                        Sign out
                      </router-link>
                    </li>
                  </ul>
                  <ul
                    v-else
                    class="py-6 mt-6 w-64 text-left bg-white shadow-2xl xl:border"
                  >
                    <li>
                      <div class="px-6 pb-2">
                        <div class="flex items-center">
                          <div class="flex overflow-hidden flex-col leading-tight">
                            <span class="font-semibold text-gray-800 truncate">{{
                              name
                            }}</span>
                            <span class="text-xs text-gray-600 truncate">{{
                              email
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                    <li class="pt-3 mt-3 border-t border-gray-300">
                      <router-link
                        :to="{ name: 'settings-profile' }"
                        class="inline-block px-6 py-2 text-gray-600"
                      >
                        Profile
                      </router-link>
                    </li>
                    <li>
                      <router-link
                        :to="billingSettingsRoute"
                        class="inline-block px-6 py-2 text-gray-600"
                      >
                        Plan
                      </router-link>
                    </li>
                    <li v-if="(isPAYG && paygSubscription) || isPerCandidate">
                      <router-link
                        :to="{ name: 'settings-payg-usage' }"
                        class="inline-block px-6 py-2 text-gray-600"
                      >
                        Usage
                      </router-link>
                    </li>
                    <li class="pt-3 mt-3 border-t border-gray-300">
                      <a
                        :href="supportUrl"
                        target="_blank"
                        class="inline-block px-6 py-2 text-gray-600"
                      >
                        Support
                      </a>
                    </li>
                    <li>
                      <a
                        href="/contact/"
                        class="inline-block px-6 py-2 text-gray-600"
                      >
                        Contact
                      </a>
                    </li>
                    <li>
                      <a
                        href="/feedback/"
                        class="inline-block px-6 py-2 text-gray-600"
                      >
                        Feedback
                      </a>
                    </li>
                    <li>
                      <router-link
                        :to="{ name: 'sign-out' }"
                        class="block px-6 pt-2 mt-0 text-gray-600"
                      >
                        Sign out
                      </router-link>
                    </li>
                  </ul>
                </template>
              </Dropdown>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  </div>
</template>

<script>
import { accountSections } from '@utils/accountSections'
import Dropdown from '@components/Dropdown'
import HeaderLink from '@components/HeaderLink'

import pluralize from 'pluralize'
import { differenceInDays } from 'date-fns'
import { mapGetters } from 'vuex'
import { nameToInitials } from '@utils/nameToInitials'

export default {
  components: {
    Dropdown,
    HeaderLink
  },

  data() {
    return {
      nameToInitials,
      accountSections,
      satellite: process.env.VUE_APP_SATELLITE,
      supportUrl: process.env.VUE_APP_SUPPORT_URL,
      isOpen: false,
      isChildOrganisationsOpen: null
    }
  },

  computed: {
    ...mapGetters({
      childOrganisation: 'employers/childOrganisation',
      childOrganisations: 'employers/childOrganisations',
      email: 'employers/email',
      hasActiveSubscription: 'subscriptions/hasActiveSubscription',
      hasOrganisationLoaded: 'organisations/hasOrganisationLoaded',
      hasPaygUnlockedExams: 'organisations/hasPaygUnlockedExams',
      hasSubscriptionLoaded: 'subscriptions/hasSubscriptionLoaded',
      haveSubscriptionsLoaded: 'subscriptions/haveSubscriptionsLoaded',
      inTrial: 'organisations/inTrial',
      isAuthorised: 'employers/isAuthorised',
      isPAYG: 'organisations/isPAYG',
      name: 'employers/name',
      paygSubscription: 'subscriptions/paygSubscription',
      isPerCandidate: 'organisations/isPerCandidate',
      candidateBilling: 'subscriptions/candidateBilling',
      specialFeatures: 'organisations/specialFeatures',
      trialEndedAt: 'organisations/trialEndedAt',
      unlimitedResults: 'organisations/unlimitedResults'
    }),

    billingSettingsRoute() {
      return { name: this.isPerCandidate ? 'settings-billing' : 'settings-payg-plan' }
    },

    siteLogo() {
      if (this.satellite === 'testcandidates' && this.isAuthorised) {
        return () => import('@components/SiteLogos/testcandidates-symbol')
      }
      return () => import('@components/SiteLogos/' + this.satellite)
    },

    /**
     * @return {string}
     */
    childOrganisationId() {
      return localStorage.getItem('employer.childOrganisationId')
    },

    /**
     * @return {Boolean}
     */
    usesPlansMenuItem() {
      return this.$usesFeature('plans-menu-item')
    },

    /**
     * @return {Boolean}
     */
    usesAssessments() {
      return this.$usesFeature('assessments')
    },

    /**
     * @return {Boolean}
     */
    usesAptitudePage() {
      return this.$usesFeature('aptitude')
    },

    /**
     * @return {Boolean}
     */
    usesScience() {
      return this.$usesFeature('science')
    },

    /**
     * @return {Boolean}
     */
    usesCandidatesList() {
      return this.$usesFeature('candidates-list')
    },

    /**
     * @return {Boolean}
     */
    usesJobsLabels() {
      return this.$usesFeature('jobs-labels')
    },

    /**
     * @return {Boolean}
     */
    usesDisableNewSubscriptions() {
      return this.$usesFeature('disable-new-subscriptions')
    },

    /**
     * @return {Boolean}
     */
    usesExternalSupport() {
      return this.$usesFeature('external-support')
    },

    /**
     * @return {Boolean}
     */
    usesFaqForSupport() {
      return this.$usesFeature('faq-support')
    },

    /**
     * @return {Number}
     */
    trialDaysRemaining() {
      return differenceInDays(new Date(this.trialEndedAt), new Date())
    },

    /**
     * @return {String}
     */
    trialText() {
      if (!this.inTrial) {
        return `Your trial has expired`
      }

      if (this.trialDaysRemaining === 0) {
        return `Your trial ends today`
      }

      return `${this.trialDaysRemaining} ${pluralize('days', this.trialDaysRemaining)} left`
    }
  },

  watch: {
    $route() {
      this.isOpen = false
    }
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen
    },

    /**
     * Organisation has been changed by the user
     *
     * @param {Object} childOrganisation
     */
    changeChildOrganisation(childOrganisation) {
      this.$store.dispatch('employers/setChildOrganisationId', childOrganisation.id).then(() => {
        location.reload()
      })
    },

    beforeEnter(el) {
      el.style.height = '0'
    },
    enter(el) {
      el.style.height = el.scrollHeight + 'px'
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + 'px'
    },
    leave(el) {
      el.style.height = '0'
    }
  }
}
</script>
